import {
  TableView,
  TableHeader,
  TableBody,
  Row,
  Cell,
  Column,
  Image,
  Flex,
  Text,
} from '@adobe/react-spectrum';
import { Loading } from 'components/basic/loading';
import { Overlay } from 'components/basic/overlay';
import { useWorkflowAppsContext } from 'components/providers/workflowAppsContext';
import { useEffect } from 'react';
import styled from 'styled-components';
import { SettingButton } from './settingButton';
import { DateCellRenderer } from './common/dateCellRenderer';
import Copy from '@spectrum-icons/workflow/Copy';
import { CopyTextButton } from 'components/basic/copyTextButton';
const MessageLayout = styled.div`
  margin-top: 58px;
  text-align: center;
`;

const TableTitle = styled.div`
  display: inline-block;
  font-size: 16px;
  margin-top: 10px;
  margin-left: 12px;
  font-weight: bold;
`;

const TextLayout = styled.div`
  align-items: center;
  display: flex;
  positoion: relative;
`;

const CopyButtonLayout = styled.button`
  position: absolute;
  left: 300px;
  border-style: none;
  background-color: rgba(255, 255, 255, 0);
`;

type TableHeaderColumn = {
  id: string;
  name: string;
  width?: number;
  allowsResizing?: boolean;
};

interface AppListProps {
  setExpirationFlg: Function;
}

export const AppList = (props: AppListProps) => {
  const workflowAppsContext = useWorkflowAppsContext();

  // 有効期限が来月か判定
  const checkExpirationMonth = (endingAt: string | null) => {
    // endingAt にデータがない場合は無期限のためfalseを返す
    if (!endingAt || endingAt === '-') return false;

    // YYYYMM の形に整形して比較する
    const endingAtDate = new Date(endingAt);
    const endingAtMonth = `${endingAtDate.getFullYear()}${(
      '0' + endingAtDate.getMonth()
    ).slice(-2)}`;

    // 翌月の年月を YYYYMM の形に整形したデータを用意
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    const expirationMonth = `${date.getFullYear()}${(
      '0' + date.getMonth()
    ).slice(-2)}`;

    // endingAt が翌月となっているか確認
    return expirationMonth === endingAtMonth;
  };

  useEffect(() => {
    // ワークフローアプリ一覧取得
    workflowAppsContext.getWorkflowApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 翌月に契約が切れるライセンスがあるか確認する
  useEffect(() => {
    if (workflowAppsContext.loading || workflowAppsContext.error) {
      props.setExpirationFlg(false);
      return;
    }

    // workflowApps 内に endingAt の値が翌月のデータが１つでもあればtrueとなる
    const expirationMonthFlg = workflowAppsContext.workflowApps.some(
      (workflowApp) => {
        return checkExpirationMonth(workflowApp.endingAt);
      },
    );

    // 翌月有効期限が切れるアプリがあるかないかのフラグをworkflowList.tsxに渡す
    props.setExpirationFlg(expirationMonthFlg);
  }, [props, workflowAppsContext]);

  if (!workflowAppsContext.loading && workflowAppsContext.error) {
    return <MessageLayout>アプリの取得に失敗しました</MessageLayout>;
  } else if (
    !workflowAppsContext.loading &&
    workflowAppsContext.workflowApps.length === 0
  ) {
    return <MessageLayout>登録されているアプリはありません</MessageLayout>;
  }

  const columns = [
    { id: 'iconUrl', name: '', width: 150 },
    { id: 'name', name: 'アプリ名', width: 300, allowsResizing: true },
    { id: 'executeKey', name: 'execute-Key', width: 410, allowsResizing: true },
    { id: 'startingDate', name: '登録日時', width: 250 },
    { id: 'endingDate', name: '利用期限', width: 250 },
    { id: 'settingButton', name: '', width: 150 },
    { id: 'Note', name: '' },
  ] as TableHeaderColumn[];

  return (
    <div>
      {workflowAppsContext.loading && (
        <Overlay>
          <Loading />
        </Overlay>
      )}
      <TableTitle>利用可能アプリ一覧</TableTitle>
      <TableView data-testid="app-list" aria-label="app-list">
        {/* テーブルヘッダー */}
        <TableHeader columns={columns}>
          {(column) => (
            <Column
              key={column.id}
              defaultWidth={column.width}
              allowsResizing={column.allowsResizing}
              minWidth={column.id === 'executeKey' ? 350 : undefined}
              isRowHeader={['name', 'executeKey'].includes(column.id)}
            >
              {column.name}
            </Column>
          )}
        </TableHeader>
        {/* テーブルボディ */}
        <TableBody items={workflowAppsContext.workflowApps}>
          {(item) => (
            <Row>
              <Cell>
                <Flex width="30px" height="30px">
                  {item['iconData'] && <Image src={item['iconData']} alt="" />}
                </Flex>
              </Cell>
              <Cell>{item['name']}</Cell>
              <Cell>
                <TextLayout>
                  {item['executeKey']}
                  <CopyButtonLayout>
                    <CopyTextButton text={item.executeKey}>
                      <Copy />
                    </CopyTextButton>
                  </CopyButtonLayout>
                </TextLayout>
              </Cell>
              <Cell>
                <DateCellRenderer date={item['createdAt']!} />
              </Cell>
              <Cell>
                {checkExpirationMonth(item['endingAt']) ? (
                  <DateCellRenderer
                    date={item['endingAt']}
                    color={'red'}
                    fontWeight={'bold'}
                  />
                ) : (
                  <DateCellRenderer date={item['endingAt']} />
                )}
              </Cell>
              <Cell>
                {/* TODO: 連携Md未対応 WF 判定検討 */}
                <SettingButton
                  workflowApp={item}
                  disabled={!item['enableFlg']}
                />
              </Cell>
              <Cell>
                {item['enableFlg'] ? (
                  <></>
                ) : (
                  <>利用可能なアプリ数を超過しています。</>
                )}
              </Cell>
            </Row>
          )}
        </TableBody>
      </TableView>
    </div>
  );
};
